import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { RepaymentsApi } from '../tokens/repayments.token';
import {
  EarlyRepayQuoteRequest,
  EarlyRepayQuoteResponse,
  RepayRequest,
  RepayResponse,
  RepaymentDateChangeRequest,
} from '../models/financing-servicing.model';

/**
 * REST-only implementation of `RepaymentsApi` (EPIC-4 / EM-500, Story 10 — EM-510).
 *
 * Calls the Story-5 F-06 repayment writes (loan-id-keyed) under `/services/embedded/api/loans`.
 * `repay()` attaches `X-SCA-Token` (payment_initiation) when a token is supplied — required by the
 * backend only for a full early repayment or a larger-than-scheduled amount; a scheduled Pay-Now
 * needs none. Rides the Keycloak bearer. Rule 07 / Rule 10 — no Mock. Rule 09 — errors propagate.
 */
@Injectable()
export class RepaymentsRestService implements RepaymentsApi {
  private readonly http = inject(HttpClient);

  repay(loanId: string, request: RepayRequest, scaToken?: string): Observable<RepayResponse> {
    const headers = scaToken ? new HttpHeaders({ 'X-SCA-Token': scaToken }) : undefined;
    return this.http.post<RepayResponse>(`${API_PATHS.LOANS}/${encodeURIComponent(loanId)}/repay`, request, { headers });
  }

  earlyRepayQuote(loanId: string, request: EarlyRepayQuoteRequest): Observable<EarlyRepayQuoteResponse> {
    return this.http.post<EarlyRepayQuoteResponse>(
      `${API_PATHS.LOANS}/${encodeURIComponent(loanId)}/early-repay-quote`,
      request,
    );
  }

  requestDateChange(
    loanId: string,
    request: RepaymentDateChangeRequest,
  ): Observable<{ modificationReference: string }> {
    return this.http.post<{ modificationReference: string }>(
      `${API_PATHS.LOANS}/${encodeURIComponent(loanId)}/request-date-change`,
      request,
    );
  }
}
